import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { ProtocolMessage } from '../../../types/protocols';

const ProtocolMessageLayout: React.FC<{ protocol: ProtocolMessage }> = ({ protocol }) => {
    const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleCopy = async (value: any, idx: string) => {
        try {
            const textToCopy = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
            await navigator.clipboard.writeText(textToCopy);
            setCopiedIndex(idx);
            enqueueSnackbar('Текст скопирован!', { variant: 'success' });
            setTimeout(() => setCopiedIndex(null), 1000);
        } catch (e) {
            // fallback
        }
    };

    // Функция для безопасного отображения значений полей
    const renderFieldValue = (value: any) => {
        if (typeof value === 'object' && value !== null) {
            // Если значение - объект, отображаем каждое поле на новой строке
            return (
                <div style={{ marginLeft: 8 }}>
                    {Object.entries(value).map(([key, val], index) => (
                        <div key={key} style={{ marginBottom: 2 }}>
                            <span style={{ fontWeight: 500, color: '#666' }}>{key}:</span>
                            <span style={{ marginLeft: 4 }}>{String(val)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        // Если значение - примитив, отображаем как есть
        return <span>{String(value)}</span>;
    };

    // Проверяем, что protocol существует и имеет нужную структуру
    if (!protocol || !protocol.protocol || !protocol.protocol.sections) {
        return (
            <div style={{ padding: '16px', color: '#666', fontStyle: 'italic' }}>
                Данные протокола недоступны или имеют неверный формат
            </div>
        );
    }

    return (
        <div>
            {protocol.protocol.sections
                .filter((section: any) => {
                    // Получаем имя секции (поддерживаем label вместо name)
                    const sectionName = section.name || section.label;
                    
                    // Проверяем, есть ли в секции поля с заполненными значениями
                    if (section.fields) {
                        // Старый вариант - когда есть массив fields
                        return section.fields.some((field: any) => {
                            const value = field.value;
                            return value && 
                                value !== 'Данные отсутствуют' && 
                                (typeof value !== 'string' || value.trim() !== '');
                        });
                    } else if (section.value) {
                        // Новый вариант - когда есть одно значение value
                        return section.value && 
                            section.value !== 'Данные отсутствуют' && 
                            (typeof section.value !== 'string' || section.value.trim() !== '');
                    }
                    return false;
                })
                .map((section: any, sectionIndex: number) => {
                    // Получаем имя секции (поддерживаем label вместо name)
                    const sectionName = section.name || section.label;
                    const sectionKey = sectionName || `section-${sectionIndex}`;
                    
                    return (
                        <div key={sectionKey} style={{ marginBottom: 16 }}>
                            {/* Если элемент имеет label вместо name и value, отображаем как секцию с полем */}
                            {section.label && !section.name && section.value !== undefined ? (
                                <>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{section.label}</div>
                                    <div style={{ marginBottom: 4 }}>
                                        <span
                                            style={{ cursor: 'pointer', color: copiedIndex === sectionKey ? '#1e6fff' : undefined, transition: 'color 0.2s' }}
                                            title={copiedIndex === sectionKey ? 'Скопировано!' : 'Кликните для копирования'}
                                            onClick={(e) => { e.stopPropagation(); handleCopy(section.value, sectionKey); }}
                                        >
                                            {renderFieldValue(section.value)}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{sectionName}</div>
                                    {section.fields ? (
                                        // Старый вариант - рендерим поля из массива fields
                                        section.fields
                                            .filter((field: any) => {
                                                const value = field.value;
                                                return value && 
                                                    value !== 'Данные отсутствуют' && 
                                                    (typeof value !== 'string' || value.trim() !== '');
                                            })
                                            .map((field: any, idx: number) => {
                                                const uniqueIdx = sectionKey + '-' + field.label;
                                                return (
                                                    <div key={field.label} style={{ marginBottom: 4 }}>
                                                        <span style={{ fontWeight: 500 }}>{field.label}:</span>
                                                        <span
                                                            style={{ marginLeft: 8, cursor: 'pointer', color: copiedIndex === uniqueIdx ? '#1e6fff' : undefined, transition: 'color 0.2s' }}
                                                            title={copiedIndex === uniqueIdx ? 'Скопировано!' : 'Кликните для копирования'}
                                                            onClick={(e) => { e.stopPropagation(); handleCopy(field.value, uniqueIdx); }}
                                                        >
                                                            {renderFieldValue(field.value)}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                    ) : (
                                        // Новый вариант - рендерим одно значение value
                                        <div style={{ marginBottom: 4 }}>
                                            <span
                                                style={{ cursor: 'pointer', color: copiedIndex === sectionKey ? '#1e6fff' : undefined, transition: 'color 0.2s' }}
                                                title={copiedIndex === sectionKey ? 'Скопировано!' : 'Кликните для копирования'}
                                                onClick={(e) => { e.stopPropagation(); handleCopy(section.value, sectionKey); }}
                                            >
                                                {renderFieldValue(section.value)}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
        </div>
    );
};

export default ProtocolMessageLayout; 